import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import { APP_TIMEZONE } from '../config';
import { useAuth } from '../context/AuthContext';
import CondoLayout from './CondoLayout';

function BulletinsContent({ condo }) {
  const { getToken } = useAuth();
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBulletins = async () => {
      if (!condo?._id) return;

      try {
        const response = await axios.get(`${getApiBaseUrl()}/api/bulletins/condo/${condo._id}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        setBulletins(response.data.bulletins || []);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch bulletins:', err);
        setError('Failed to load bulletins. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBulletins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condo]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: APP_TIMEZONE
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: APP_TIMEZONE
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Loading bulletins...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {bulletins.length === 0 && !error && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <div className="mb-4 text-muted-foreground flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
            </svg>
          </div>
          <p className="text-foreground text-base md:text-lg font-semibold">No bulletins yet</p>
          <p className="text-muted-foreground text-sm md:text-base mt-2">
            Check back later for updates and announcements.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {bulletins.map((bulletin) => (
          <div
            key={bulletin._id}
            className="bg-card border border-border rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className="p-4 md:p-6">
              <div className="flex-1 mb-3">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h2 className="text-lg font-semibold text-foreground">
                    {bulletin.title}
                  </h2>
                  {bulletin.audience === 'owners' && (
                    <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">Owners Only</span>
                  )}
                </div>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Posted on {formatDate(bulletin.createdAt)}
                </p>
              </div>

              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                {bulletin.description}
              </p>

              {(bulletin.calendarEvents || []).map((event) => (
                <div key={event._id} className="mt-4 p-3 md:p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="text-primary">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground text-sm md:text-base mb-2">
                        {event.title}
                      </h3>
                      <div className="space-y-1 text-xs md:text-sm text-muted-foreground">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="font-semibold text-foreground">Start:</span>
                          <span className="break-all">{formatDateTime(event.startTime)}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="font-semibold text-foreground">End:</span>
                          <span className="break-all">{formatDateTime(event.endTime)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {bulletins.length > 0 && (
        <div className="mt-6 md:mt-8 text-center text-xs md:text-sm text-muted-foreground">
          Showing bulletins from the last 6 months
        </div>
      )}
    </div>
  );
}

function BulletinPage() {
  return (
    <CondoLayout>
      {({ condo }) => <BulletinsContent condo={condo} />}
    </CondoLayout>
  );
}

export default BulletinPage;
